/**
 * Authentication context for managing auth state across the web app
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { type User } from '../services/firebase';
import authService from '../services/authService';
import { fetchCurrentUserInfo, type CurrentUserInfo } from '../API/Admin/AdminAPI';

export type UserRole = 'admin' | 'editor' | 'viewer' | null;

interface AuthContextType {
  user: User | null;
  userRole: UserRole;
  isLoading: boolean;
  isRoleLoading: boolean;
  isAuthenticated: boolean;
  isAuthorized: boolean; // True if user is in allowed users table
  isUnauthorized: boolean; // True if user signed in but NOT in allowed users table
  isAdmin: boolean;
  isEditor: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  getToken: () => Promise<string>;
  refreshUserRole: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRole>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRoleLoading, setIsRoleLoading] = useState(false);
  const [wasRejected, setWasRejected] = useState(false); // User tried to log in but not in allowed table

  // Function to fetch user role from backend
  const fetchUserRole = async () => {
    try {
      setIsRoleLoading(true);
      setWasRejected(false);
      console.log('🔄 [AuthProvider] Fetching user role from backend...');
      const userInfo = await fetchCurrentUserInfo();
      console.log('✅ [AuthProvider] User role fetched:', userInfo.role);
      setUserRole(userInfo.role);
      setWasRejected(false);
    } catch (error: any) {
      console.error('❌ [AuthProvider] Failed to fetch user role:', error);

      // Extract status code from axios error
      const statusCode = error.response?.status;
      console.log('🔍 [AuthProvider] Error status code:', statusCode);
      console.log('🔍 [AuthProvider] Error message:', error.message);

      // 401 = user not authorized by API Gateway (not in allowed users table)
      // 403 = user authenticated but forbidden
      // 404 = user not found in table
      if (statusCode === 401 || statusCode === 403 || statusCode === 404) {
        console.log('🚫 [AuthProvider] User not in allowed users table - ACCESS DENIED');
        setUserRole(null);
        setWasRejected(true);
      } else {
        // For other errors (network, etc.), also reject for security
        console.log('🚫 [AuthProvider] Error verifying user - ACCESS DENIED (error type:', error.name || 'unknown', ')');
        setUserRole(null);
        setWasRejected(true);
      }
    } finally {
      setIsRoleLoading(false);
    }
  };

  // Refresh user role (can be called manually after admin changes)
  const refreshUserRole = async () => {
    if (user) {
      await fetchUserRole();
    }
  };

  useEffect(() => {
    console.log('🔄 [AuthProvider] Setting up auth state listener');

    // Subscribe to auth state changes
    const unsubscribe = authService.onAuthStateChange((authUser) => {
      console.log('🔔 [AuthProvider] Auth state changed:', authUser?.email || 'No user');
      setUser(authUser);
      setIsLoading(false);

      // Fetch user role when authenticated
      if (authUser) {
        fetchUserRole();
      } else {
        setUserRole(null);
      }
    });

    // Check current auth state
    const currentUser = authService.getCurrentUser();
    if (currentUser) {
      console.log('✅ [AuthProvider] Current user found:', currentUser.email);
      setUser(currentUser);
      fetchUserRole();
    } else {
      console.log('❌ [AuthProvider] No current user');
    }
    setIsLoading(false);

    // Cleanup subscription
    return unsubscribe;
  }, []);

  const signIn = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await authService.signInWithGoogle();
      // User state will be updated by the auth state listener
    } catch (error) {
      console.error('❌ [AuthProvider] Sign in failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await authService.signOut();
      setUserRole(null);
      setWasRejected(false);
      // User state will be updated by the auth state listener
    } catch (error) {
      console.error('❌ [AuthProvider] Sign out failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getToken = async (): Promise<string> => {
    return authService.getGoogleToken();
  };

  // User is authorized if they have a role (i.e., they're in the allowed users table)
  const isAuthorized = !!user && !!userRole && !wasRejected;
  // User is unauthorized if they signed in with Firebase but were rejected from allowed users table
  const isUnauthorized = !!user && wasRejected && !isRoleLoading;

  const value: AuthContextType = {
    user,
    userRole,
    isLoading,
    isRoleLoading,
    isAuthenticated: !!user,
    isAuthorized,
    isUnauthorized,
    isAdmin: userRole === 'admin',
    isEditor: userRole === 'editor' || userRole === 'admin',
    signIn,
    signOut,
    getToken,
    refreshUserRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;